import { useContext, useEffect, useState } from 'react';
import { Box, Typography, List, ListItem, ListItemText, Button, AppBar, Toolbar } from '@mui/material';
import { NotificationsActive } from '@mui/icons-material';
import { useDialog } from '../contexts/DialogContext';
import { WithdrawalContext } from '../contexts/WithdrawalContext';
import { formatAmount } from '../utils/utils';
import LoadingState from './LoadingState';
import Pagination from './Pagination';
import usePagination from '../hooks/usePagination';

const PayoutListing = ({ onBuy }) => {
  const { 
    withdrawals, 
    setWithdrawals, 
    fetchAvailableWithdrawals, 
    grabWithdrawal, 
    getProcessingWithdrawalId
  } = useContext(WithdrawalContext);

  const { openDialog, closeDialog } = useDialog();

  const [isLoading, setIsLoading] = useState(true);
  const [isGrabbing, setIsGrabbing] = useState(false);
  const [grabbingId, setGrabbingId] = useState(null);

  const PAGE_SIZE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const {
    data: paginatedWithdrawals,
    totalCount,
  } = usePagination({
    data: withdrawals,
    page: currentPage,
    pageSize: PAGE_SIZE,
    mode: 'client',
  });

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (withdrawals.length > 0) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      await fetchAvailableWithdrawals();
      if (!cancelled) {
        setIsLoading(false);
      }
    };

    init();

    return () => {
      cancelled = true;
    };
    // Mount-only: parent may prefetch via syncGrabTab before this component mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => setWithdrawals([]);
  }, [setWithdrawals]);

  const handleBuy = async (withdrawal) => {
    setGrabbingId(withdrawal.id);
    setIsGrabbing(true);

    const withdrawalId = await getProcessingWithdrawalId();
    if(withdrawalId){
      setGrabbingId(null);
      setIsGrabbing(false);

      openDialog(
      {
          title: '新申购提醒',
          message: '您有新的申购请求，请立即前往处理。',
          confirmText: '好的',
          onConfirm: () =>  {
              closeDialog();
          },
          showCancelButton: false
      });
      return;
    }

    try {
      const result = await grabWithdrawal(withdrawal);

      if (result.isSuccess) {
        onBuy(withdrawal.id);
      } else {
        throw new Error('Grab failed');
      }
    } catch (err) {
      setGrabbingId(null);
      setIsGrabbing(false);

      setWithdrawals([]);
      fetchAvailableWithdrawals();
    }
  };
  
  return(
    <Box 
      sx={{ 
        backgroundColor: 'var(--bg-secondary-color)',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        flex: 1,
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      <AppBar position="sticky" 
        sx={{ 
          backgroundColor: 'transparent',
          boxShadow: 'none',
        }}
      >
        <Toolbar sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          padding: '0 16px', 
          color: 'var(--primary-color)' }}
        >
          {/* Centered Text */}
          <Typography
            variant="h6"
            sx={{ flexGrow: 1, textAlign: 'center' }}
          >
            { isLoading || withdrawals.length > 0 ? '正在匹配申购' : '暂无匹配申购' }
          </Typography>

          {/* Notification Bell Icon */}
            <NotificationsActive  edge="end" color="inherit" aria-label="notifications" />
        </Toolbar>
      </AppBar>

      {isLoading ? (
        <LoadingState fullHeight/>
      ) : isGrabbing ? (
        <LoadingState fullHeight>
          <Typography sx={{ color: 'var(--primary-color)' }}>
            抢单中
          </Typography>

          <Typography sx={{ color: 'var(--primary-color)', fontSize: '0.85rem' }}>
            W{grabbingId}
          </Typography>
        </LoadingState>
      ) : (
        <>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              minHeight: 0,
              width: '100%',
            }}
          >
            {paginatedWithdrawals.length > 0 ? (
              <List sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  margin: '0 16px'
                }}
              >
                {paginatedWithdrawals.map((withdrawal, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      boxShadow: '0 4px 10px 2px rgba(0, 0, 0, 1)', 
                      borderRadius: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <ListItemText
                      primary={`${formatAmount(withdrawal.amount)}`}
                      secondary={`订单号: ${withdrawal.orderId}`}
                      sx={{ 
                        color: 'var(--primary-color)', 
                        '& p': {
                          color: 'var(--primary-color)',
                          fontSize: '0.75rem'
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={() => handleBuy(withdrawal)}
                      disabled={isGrabbing}
                      className='primary-button'
                    >
                      抢单
                    </Button>
                  </ListItem>
                ))}
              </List>
            ) : null}
          </Box>
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            onPageChange={(page) => setCurrentPage(page)}
            pageSize={PAGE_SIZE}
          />
        </>
      )}
    </Box>
  );   
};

export default PayoutListing;
